import { Box, Button, Container, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from "@mui/material";
import { cardSX, pageHeaderSX } from "../../utilities/CSS";
import { visuallyHidden } from '@mui/utils';
import TableRowsLoader, { TableLoaderProps } from "../TableRowsLoader";
import { createCsvContent, downloadCsv } from "../../utilities/HelperFunctions";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';

interface ReportTableProps {
    report_title: string,
    accessibility_title: string,
    tableHeaders: string[],
    empty_message: string,
    table_data: any[],
    render_row: (row: any) => any,
    is_loaded: boolean,
    form?: any,
    foot_notes?: any,
    hasExport?: boolean
    csvFileName?: string,
    sortableColumns?: string[],
    getColumnSortValue?: (row: any, columnKey: string) => any,
    page?: number,
    rowsPerPage?: number
}

const BackToReportsLink = () => {
  const navigate = useNavigate();

  return (
    <Link
      color='inherit'
      onClick={() => navigate('/admin/reports')}  
      underline='hover'
      sx={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '0.875rem',
        mt: 0.5,
        mb: 1.25
      }}
    >
      <ChevronLeftIcon fontSize='small' sx={{ ml: '-0.25rem', mr: 0.25 }} />
      <Typography variant='body2'>Back to Available Reports</Typography>
    </Link>
  );
};

const ReportTable = (props: ReportTableProps) => {
    const [orderBy, setOrderBy] = useState<string>('');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');

    const handleRequestSort = (property: string) => {
        if (!props.sortableColumns?.includes(property)) return;
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedAndPagedData = React.useMemo(() => {
        let sortedData = props.table_data;
        
        // Apply sorting if orderBy is set
        if (orderBy) {
            sortedData = [...props.table_data].sort((a, b) => {
                const aValue = props.getColumnSortValue ? props.getColumnSortValue(a, orderBy) : a[orderBy];
                const bValue = props.getColumnSortValue ? props.getColumnSortValue(b, orderBy) : b[orderBy];
                
                let isEmpty = (val: any) => val === null || val === undefined || val === '';
                if (isEmpty(aValue) && isEmpty(bValue)) return 0;
                if (isEmpty(aValue)) return 1;
                if (isEmpty(bValue)) return -1;
                
                const aDate = new Date(aValue);
                const bDate = new Date(bValue);
                if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
                    return order === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
                }
                
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return order === 'asc' ? aValue - bValue : bValue - aValue;
                }
                
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                
                if (aStr < bStr) return order === 'asc' ? -1 : 1;
                if (aStr > bStr) return order === 'asc' ? 1 : -1;
                return 0;
            });
        }
        
        // Apply pagination if page and rowsPerPage are provided
        if (props.page !== undefined && props.rowsPerPage !== undefined) {
            const startIndex = props.rowsPerPage * props.page;
            const endIndex = startIndex + props.rowsPerPage;
            return sortedData.slice(startIndex, endIndex);
        }
        
        return sortedData;
    }, [orderBy, props, order]);
    
    const TableLoader = ({ colsNum, rowsNum, animation = 'pulse' }: TableLoaderProps) => {
        return (
            <TableWrapper>
                <TableRowsLoader colsNum={colsNum} rowsNum={rowsNum} animation={animation} rowHeight={55} />
            </TableWrapper>
        );
    };
    
    const TableWrapper = ({ children }: { children: React.ReactNode }) => {
        const thSX = {
            color: '#1e1e1e',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap'
        };
    
        return (
            <TableContainer>
                <Table aria-label={props.accessibility_title} size='small'>
                    <Box component={'caption'} sx={visuallyHidden}>{props.accessibility_title}</Box>
                    <TableHead>
                        <TableRow>
                            {props.tableHeaders.map((tableHeader, index) => {
                                const columnKey = props.sortableColumns?.[index] || tableHeader;
                                const isSortable = props.sortableColumns?.includes(columnKey);
                                
                                return (
                                    <TableCell key={`header-${tableHeader}`} component="th" sx={thSX}>
                                        {isSortable ? (
                                            <TableSortLabel
                                                active={orderBy === columnKey}
                                                direction={orderBy === columnKey ? order : 'asc'}
                                                onClick={() => handleRequestSort(columnKey)}
                                            >
                                                {tableHeader}
                                            </TableSortLabel>
                                        ) : (
                                            tableHeader
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {children}
                    </TableBody>
                </Table>
                {props.foot_notes}
            </TableContainer>
        );
    };
    
    const TableEmpty = ({ colspan }: { colspan: number }) => {
        return (
            <TableWrapper>
                <TableRow>
                    <TableCell colSpan={colspan} sx={{ borderBottom: 'none' }}>
                        <Typography
                            variant='body1'
                            sx={{
                                // WAVE accessibility contrast error if color matches Figma color of #b8b8b8, so darken it enough to pass (grey.600 = #757575)
                                color: 'grey.600',
                                fontWeight: 500,
                                py: 3.75,
                                textAlign: 'center'
                            }}
                        >{props.empty_message}</Typography>
                    </TableCell>
                </TableRow>
            </TableWrapper>
        );
    };

    const handleExportCsv = async () => {
        downloadCsv(createCsvContent(props.table_data), props.csvFileName ? props.csvFileName : "export.csv");
    };

    return (<>
        <BackToReportsLink />
        <Typography variant='h1' sx={{ ...pageHeaderSX, mb: 4.5 }}>{props.report_title}</Typography>

        {props.form}

        <Paper sx={cardSX.paperMinHeight}>
            <Container sx={{ py: 4.5 }}>
                {props.hasExport && <Stack direction='row' alignItems='center' sx={{ mb: 3 }}>
                    <Button
                        color='inherit'
                        variant='outlined'
                        onClick={() => handleExportCsv()}
                        sx={{
                        fontSize: '1rem',
                        ml: 'auto',
                        mr: 1.25,
                        textTransform: 'none'
                        }}
                    >
                        Export
                    </Button>
                </Stack>}

                {!props.is_loaded && <TableLoader colsNum={5} rowsNum={props.table_data.length || 5} />}

                {props.is_loaded && props.table_data.length === 0 && <TableEmpty colspan={5} />}

                {props.is_loaded && props.table_data.length > 0 && (
                    <TableWrapper>
                        {sortedAndPagedData.map(row => {
                            return props.render_row(row);
                        })}
                    </TableWrapper>
                )}
            </Container>
        </Paper>
    </>);
};

export default ReportTable;